import { Incident } from '@haccp/api-client';
import { useTranslation } from 'react-i18next';
import {
  getIncidentStatusClass,
  getIncidentStatusText,
  getIncidentTypeText,
  formatIncidentDateTime,
  getIncidentDuration,
} from '../utils/incidents';

interface IncidentListProps {
  incidents: Incident[];
  isLoading?: boolean;
  onSelectIncident?: (id: string) => void;
}

export default function IncidentList({
  incidents,
  isLoading = false,
  onSelectIncident,
}: IncidentListProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-neutral-600">{t('incidents.loading')}</div>
      </div>
    );
  }

  if (incidents.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-lg font-medium text-neutral-900">{t('incidents.noIncidents')}</div>
          <p className="text-neutral-600 mt-1">{t('incidents.allSystemsNormal', 'Tous les systèmes fonctionnent normalement')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('incidents.type', 'Type')}</th>
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('incidents.status')}</th>
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('incidents.openedOn', 'Ouvert le')}</th>
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('incidents.closedOn', 'Fermé le')}</th>
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('incidents.duration', 'Durée')}</th>
            <th className="text-left px-4 py-3 font-medium text-neutral-700">{t('incidents.action', 'Action')}</th>
          </tr>
        </thead>
        <tbody>
          {incidents.map((incident) => (
            <tr key={incident.id} className="border-b border-neutral-100 hover:bg-neutral-50">
              <td className="px-4 py-3">
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {getIncidentTypeText(incident.type)}
                </span>
              </td>
              <td className="px-4 py-3">
                <span
                  className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${getIncidentStatusClass(
                    incident.status
                  )}`}
                >
                  {getIncidentStatusText(incident.status)}
                </span>
              </td>
              <td className="px-4 py-3 text-neutral-700">
                {formatIncidentDateTime(incident.openedAt)}
              </td>
              <td className="px-4 py-3 text-neutral-700">
                {incident.closedAt ? formatIncidentDateTime(incident.closedAt) : '-'}
              </td>
              <td className="px-4 py-3 text-neutral-600">
                {getIncidentDuration(incident)}
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => onSelectIncident?.(incident.id)}
                  className="px-3 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
                >
                  {t('incidents.viewDetails', 'Voir détails')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
